import { HTTPError } from 'ky';

interface ApiErrorResponse {
  message?: string;
  error?: string;
  status?: number;
}

/**
 * Extracts a user-friendly error message from an error object.
 * Handles HTTPError from ky by parsing the response body.
 *
 * @param error - The error object (can be HTTPError, Error, or unknown)
 * @param fallback - Fallback message if no message can be extracted
 * @returns A user-friendly error message
 */
export async function getErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred',
): Promise<string> {
  // Handle HTTPError from ky
  if (error instanceof HTTPError) {
    try {
      const body = await error.response.json<ApiErrorResponse>();
      // Prefer the 'message' field, fallback to 'error' field
      return body.message || body.error || fallback;
    } catch {
      // If parsing fails, use the HTTPError's message
      return error.message || fallback;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || fallback;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Fallback for unknown error types
  return fallback;
}

/**
 * Synchronous version that extracts error message without async parsing.
 * Use this when you need a synchronous error message, but note it won't
 * parse HTTPError response bodies.
 *
 * @param error - The error object
 * @param fallback - Fallback message if no message can be extracted
 * @returns A user-friendly error message
 */
export function getErrorMessageSync(
  error: unknown,
  fallback = 'An unexpected error occurred',
): string {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (typeof error === 'string') {
    return error;
  }

  return fallback;
}
